/** Number formatting for report widgets. Money arrives in cents, percentages as 0–1. */

import type { Change, MetricFormat } from './reports-api.ts';

const compact = new Intl.NumberFormat('en-ZA', { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const twoDp = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const SECONDS_PER_UNIT: [string, number][] = [
  ['d', 86_400],
  ['h', 3_600],
  ['m', 60],
];

/** AgencyAnalytics' compact duration: "5d 15h 59m 58s", "52m 39s", "8s". */
export function durationText(seconds: number): string {
  let rest = Math.round(Math.max(0, seconds));
  const parts: string[] = [];
  for (const [unit, size] of SECONDS_PER_UNIT) {
    if (rest >= size || parts.length > 0) {
      parts.push(`${Math.floor(rest / size)}${unit}`);
      rest %= size;
    }
  }
  parts.push(`${rest}s`);
  return parts.join(' ');
}

export function formatValue(
  value: number | null | undefined,
  format: MetricFormat,
  short = false,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  switch (format) {
    case 'money': {
      const rand = value / 100;
      if (short && Math.abs(rand) >= 10_000) return `R${compact.format(rand)}`;
      return Math.abs(rand) >= 1000 ? `R${whole.format(rand)}` : `R${twoDp.format(rand)}`;
    }
    case 'percent':
      return `${(value * 100).toFixed(value < 0.1 ? 2 : 1)}%`;
    case 'rating':
      return `${value.toFixed(1)}★`;
    case 'duration':
      return durationText(value);
    default:
      if (short && Math.abs(value) >= 10_000) return compact.format(value);
      return Number.isInteger(value) ? whole.format(value) : twoDp.format(value);
  }
}

export type ChangeFormat = 'PERCENT' | 'ABSOLUTE';

const FLAT_PERCENT = 0.05;

/** The absolute difference in the metric's own unit; a rate moves in percentage points. */
function absoluteText(absolute: number, format: MetricFormat): string {
  const sign = absolute > 0 ? '+' : '−';
  if (format === 'percent') return `${sign}${Math.abs(absolute * 100).toFixed(1)} pts`;
  return `${sign}${formatValue(Math.abs(absolute), format, true)}`;
}

/**
 * "+12.4%" (or "+R1,250" when the report compares in absolute terms) with a
 * tone that respects direction: a rising cost is bad news.
 */
export function formatChange(
  change: Change | null,
  higherIsBetter: boolean,
  options: { as?: ChangeFormat; format?: MetricFormat } = {},
): { text: string; tone: 'up' | 'down' | 'flat' } | null {
  if (!change) return null;
  if (options.as === 'ABSOLUTE') {
    if (change.absolute === 0) return { text: 'no change', tone: 'flat' };
    return {
      text: absoluteText(change.absolute, options.format ?? 'number'),
      tone: change.absolute > 0 === higherIsBetter ? 'up' : 'down',
    };
  }
  if (change.percent === null)
    return { text: change.absolute === 0 ? 'no change' : 'new', tone: 'flat' };
  const pct = change.percent * 100;
  if (Math.abs(pct) < FLAT_PERCENT) return { text: '0%', tone: 'flat' };
  const good = pct > 0 === higherIsBetter;
  return {
    text: `${pct > 0 ? '+' : ''}${pct.toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`,
    tone: good ? 'up' : 'down',
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function bucketLabel(bucket: string, granularity: 'day' | 'week' | 'month'): string {
  const month = MONTHS[Number(bucket.slice(5, 7)) - 1] ?? '';
  if (granularity === 'month') return `${month} ${bucket.slice(2, 4)}`;
  return `${Number(bucket.slice(8, 10))} ${month}`;
}

export function rangeLabel(range: { from: string; to: string }): string {
  const day = (iso: string) =>
    `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
  return range.from === range.to ? day(range.from) : `${day(range.from)} – ${day(range.to)}`;
}

export const PRESET_LABELS: Record<string, string> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
  LAST_N_DAYS: 'Last N days',
  LAST_WEEK: 'Last week',
  THIS_MONTH: 'This month',
  LAST_MONTH: 'Last month',
  LAST_N_MONTHS: 'Last N months',
  THIS_QUARTER: 'This quarter',
  LAST_QUARTER: 'Last quarter',
  THIS_YEAR: 'This year',
  LAST_YEAR: 'Last year',
  ALL_TIME: 'All time',
  CUSTOM: 'Custom',
};

export const COMPARE_LABELS: Record<string, string> = {
  NONE: 'No comparison',
  PREVIOUS_PERIOD: 'Previous period',
  PREVIOUS_YEAR: 'Previous year',
  CUSTOM: 'Custom period',
};
